import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { footerLogo } from '../assets/images'

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
    const footerColsRef = useRef(null);
    const newsletterRef = useRef(null);
    const socialIconsRef = useRef(null);
    const bottomSecRef = useRef(null);

    useEffect(() => {
        // Animate footer columns
        const footerCols = footerColsRef.current?.querySelectorAll('.footer-col');
        if (footerCols) {
            gsap.fromTo(footerCols,
                {
                    y: 30,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    stagger: 0.1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: footerColsRef.current,
                        start: "top 95%",
                        toggleActions: "play none none none"
                    }
                }
            );

            // Animate list items in each column
            footerCols.forEach(col => {
                const items = col.querySelectorAll('li');
                gsap.fromTo(items,
                    {
                        x: -20,
                        opacity: 0
                    },
                    {
                        x: 0,
                        opacity: 1,
                        duration: 0.5,
                        stagger: 0.05,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: col,
                            start: "top 90%",
                            toggleActions: "play none none none"
                        }
                    }
                );
            });
        }

        // Animate newsletter section
        if (newsletterRef.current) {
            const newsletterElements = newsletterRef.current.querySelectorAll('*');
            gsap.fromTo(newsletterElements,
                {
                    y: 20,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.3,
                    stagger: 0.1,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: newsletterRef.current,
                        start: "top 90%",
                        toggleActions: "play none none none"
                    }
                }
            );
        }

        // Animate social icons
        const socialIcons = socialIconsRef.current?.querySelectorAll('a');
        if (socialIcons) {
            gsap.fromTo(socialIcons,
                {
                    scale: 0.8,
                    opacity: 0
                },
                {
                    scale: 1,
                    opacity: 1,
                    duration: 0.5,
                    stagger: 0.05,
                    ease: "back.out(1)",
                    scrollTrigger: {
                        trigger: socialIconsRef.current,
                        start: "top 90%",
                        toggleActions: "play none none none",
                        once: true,
                        fastScrollEnd: true
                    }
                }
            );

            // Add hover animation to social icons
            socialIcons.forEach(icon => {
                icon.addEventListener('mouseenter', () => {
                    gsap.to(icon, {
                        scale: 1.2,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                });
                
                icon.addEventListener('mouseleave', () => {
                    gsap.to(icon, {
                        scale: 1,
                        duration: 0.3,
                        ease: "power2.out"
                    });
                });
            });
        }

        // Animate bottom section
        if (bottomSecRef.current) {
            gsap.fromTo(bottomSecRef.current,
                {
                    y: 20,
                    opacity: 0
                },
                {
                    y: 0,
                    opacity: 1,
                    duration: 0.8,
                    ease: "power2.out",
                    scrollTrigger: {
                        trigger: bottomSecRef.current,
                        start: "top 95%",
                        toggleActions: "play none none none"
                    }
                }
            );
        }
    }, []);

    return (
        <footer className="footer">
            <div className="container footer-bg">
                <div ref={footerColsRef} className="flex footer-content">
                    <div className="footer-col">
                        <span className="col-heading text-body">Product</span>
                        <ul>
                            <li className="text-body"><a href="#">Core features</a></li>
                            <li className="text-body"><a href="#">Extensions</a></li>
                            <li className="text-body"><a href="#">Marketing</a></li>
                            <li className="text-body"><a href="#">Sales Management</a></li>
                            <li className="text-body"><a href="#">Software</a></li>
                        </ul>
                    </div>
                    <div className="footer-col">
                        <span className="col-heading text-body">Company</span>
                        <ul>
                            <li className="text-body"><a href="#">Register</a></li>
                            <li className="text-body"><a href="#">14-Day Trial</a></li>
                            <li className="text-body"><a href="#">About Us</a></li>
                            <li className="text-body"><a href="#">Careers</a></li>
                            <li className="text-body"><a href="#">Latest News</a></li>
                        </ul>
                    </div>
                    <div className="footer-col">
                        <span className="col-heading text-body">API</span>
                        <ul>
                            <li className="text-body"><a href="#">Getting Started</a></li>
                            <li className="text-body"><a href="#">API Reference</a></li>
                            <li className="text-body"><a href="#">Developer Guide</a></li>
                            <li className="text-body"><a href="#">Careers</a></li>
                            <li className="text-body"><a href="#">GitHub</a></li>
                        </ul>
                    </div>
                    <div className="footer-col">
                        <span className="col-heading text-body">Assistance</span>
                        <ul>
                            <li className="text-body"><a href="#">Help Center</a></li>
                            <li className="text-body"><a href="#">Support Requests</a></li>
                            <li className="text-body"><a href="#">FAQs</a></li>
                            <li className="text-body"><a href="#">Licensing</a></li>
                        </ul>
                    </div>
                    <div ref={newsletterRef} className="footer-col">
                        <span className="col-heading text-body unset">Join Our Newsletter</span>
                        <div className="input-with-icon">
                            <div className="icon">
                                <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M2.96615 3.18115H13.6328C14.3661 3.18115 14.9661 3.78115 14.9661 4.51449V12.5145C14.9661 13.2478 14.3661 13.8478 13.6328 13.8478H2.96615C2.23281 13.8478 1.63281 13.2478 1.63281 12.5145V4.51449C1.63281 3.78115 2.23281 3.18115 2.96615 3.18115Z" stroke="#CECECE" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M14.9661 4.51465L8.29948 9.18132L1.63281 4.51465" stroke="#CECECE" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                            </div>
                            <input type="text" className="input-field input-field--regular input-field--primary" placeholder="Email" />
                        </div>
                        <div className="button-wrapper">
                            <button className="btn btn--regular btn--primary w-full">
                                Subscribe
                            </button>
                        </div>
                        <div ref={socialIconsRef} className="icons flex align-center">
                            <div>
                                <a href=""><svg width="11" height="17" viewBox="0 0 11 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9.06303 9.51465L9.50749 6.61902H6.72853V4.73996C6.72853 3.94777 7.11673 3.17559 8.36134 3.17559H9.6247V0.710273C9.6247 0.710273 8.47823 0.514648 7.38209 0.514648C5.09353 0.514648 3.59763 1.90152 3.59763 4.41215V6.61902H1.05371V9.51465H3.59763V16.5146H6.72853V9.51465H9.06303Z" fill="white" />
                                </svg>
                                </a>
                            </div>
                            <div><a href=""><svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M14.7352 5.25565C14.7454 5.39778 14.7454 5.53993 14.7454 5.68206C14.7454 10.0171 11.4459 15.012 5.41541 15.012C3.55754 15.012 1.83166 14.4739 0.379883 13.5399C0.643852 13.5704 0.897633 13.5805 1.17176 13.5805C2.70473 13.5805 4.11591 13.0628 5.24282 12.1795C3.8012 12.1491 2.59307 11.2049 2.17682 9.9054C2.37988 9.93584 2.58291 9.95615 2.79613 9.95615C3.09054 9.95615 3.38498 9.91553 3.65907 9.8445C2.15654 9.5399 1.0296 8.22012 1.0296 6.62621V6.58562C1.46613 6.82928 1.97379 6.98156 2.51182 7.00184C1.62857 6.413 1.04991 5.40793 1.04991 4.27087C1.04991 3.66175 1.21232 3.10337 1.4966 2.61606C3.11082 4.6059 5.53723 5.90537 8.25801 6.04753C8.20726 5.80387 8.17679 5.55009 8.17679 5.29628C8.17679 3.48915 9.63873 2.01709 11.456 2.01709C12.4001 2.01709 13.2529 2.41303 13.8519 3.05262C14.593 2.9105 15.3037 2.63637 15.9331 2.26075C15.6894 3.02218 15.1717 3.66178 14.4915 4.06784C15.1514 3.99681 15.791 3.81403 16.3798 3.56025C15.9332 4.20996 15.3748 4.78862 14.7352 5.25565Z" fill="white" />
                            </svg>
                            </a>
                            </div>
                            <div><a href=""><svg width="15" height="17" viewBox="0 0 15 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M3.55312 14.5148H0.650227V5.16795H3.55312V14.5148ZM2.10011 3.89295C1.17186 3.89295 0.418945 3.1242 0.418945 2.19607C0.418945 1.75026 0.596067 1.32271 0.911347 1.00747C1.22663 0.692235 1.65424 0.515137 2.10011 0.515137C2.54598 0.515137 2.97359 0.692235 3.28887 1.00747C3.60415 1.32271 3.78127 1.75026 3.78127 2.19607C3.78127 3.1242 3.02805 3.89295 2.10011 3.89295ZM14.4177 14.5148H11.5211V9.96482C11.5211 8.88045 11.4992 7.48982 10.0118 7.48982C8.50254 7.48982 8.27126 8.66795 8.27126 9.8867V14.5148H5.37149V5.16795H8.15561V6.44295H8.19624C8.5838 5.70857 9.53049 4.93357 10.9429 4.93357C13.8808 4.93357 14.4208 6.86795 14.4208 9.38045V14.5148H14.4177Z" fill="white" />
                            </svg>
                            </a>
                            </div>
                            <div>
                                <a href="">
                                    <svg width="19" height="17" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.6776 4.39224C17.4813 3.65318 16.903 3.07112 16.1686 2.87359C14.8375 2.51465 9.50006 2.51465 9.50006 2.51465C9.50006 2.51465 4.16262 2.51465 2.83152 2.87359C2.09716 3.07115 1.51878 3.65318 1.32248 4.39224C0.96582 5.73184 0.96582 8.52677 0.96582 8.52677C0.96582 8.52677 0.96582 11.3217 1.32248 12.6613C1.51878 13.4004 2.09716 13.9582 2.83152 14.1557C4.16262 14.5146 9.50006 14.5146 9.50006 14.5146C9.50006 14.5146 14.8375 14.5146 16.1686 14.1557C16.903 13.9582 17.4813 13.4004 17.6776 12.6613C18.0343 11.3217 18.0343 8.52677 18.0343 8.52677C18.0343 8.52677 18.0343 5.73184 17.6776 4.39224ZM7.75441 11.0644V5.98918L12.2155 8.52684L7.75441 11.0644Z" fill="white" />
                                    </svg>
                                </a>
                            </div>
                            <div>
                                <a href=""> <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.7337 12.9334C5.7337 12.9959 5.6618 13.0459 5.57115 13.0459C5.468 13.0553 5.3961 13.0053 5.3961 12.9334C5.3961 12.8709 5.468 12.8209 5.55865 12.8209C5.65242 12.8115 5.7337 12.8615 5.7337 12.9334ZM4.76155 12.7928C4.73967 12.8553 4.80218 12.9271 4.89596 12.9459C4.97723 12.9771 5.07101 12.9459 5.08976 12.8834C5.10852 12.8209 5.04913 12.749 4.95535 12.7209C4.87408 12.699 4.78343 12.7303 4.76155 12.7928ZM6.14319 12.7396C6.05254 12.7615 5.99002 12.8209 5.9994 12.8928C6.00877 12.9553 6.09005 12.9959 6.18382 12.974C6.27448 12.9521 6.33699 12.8928 6.32762 12.8303C6.31824 12.7709 6.23384 12.7303 6.14319 12.7396ZM8.20002 0.764648C3.86442 0.764648 0.547852 4.05527 0.547852 8.38965C0.547852 11.8553 2.72972 14.8209 5.84623 15.8646C6.24634 15.9365 6.38701 15.6896 6.38701 15.4865C6.38701 15.2928 6.37763 14.224 6.37763 13.5678C6.37763 13.5678 4.18951 14.0365 3.73 12.6365C3.73 12.6365 3.37365 11.7271 2.86101 11.4928C2.86101 11.4928 2.14518 11.0021 2.91102 11.0115C2.91102 11.0115 3.68937 11.074 4.11761 11.8178C4.80218 13.024 5.94938 12.6771 6.39638 12.4709C6.46828 11.9709 6.67146 11.624 6.89653 11.4178C5.14916 11.224 3.38616 10.9709 3.38616 7.96465C3.38616 7.10527 3.62372 6.67402 4.12387 6.12402C4.04259 5.9209 3.77689 5.0834 4.20514 4.00215C4.85845 3.79902 6.362 4.8459 6.362 4.8459C6.98718 4.6709 7.65924 4.58027 8.32506 4.58027C8.99087 4.58027 9.66294 4.6709 10.2881 4.8459C10.2881 4.8459 11.7917 3.7959 12.445 4.00215C12.8732 5.08652 12.6075 5.9209 12.5262 6.12402C13.0264 6.67715 13.3327 7.1084 13.3327 7.96465C13.3327 10.9803 11.4916 11.2209 9.74421 11.4178C10.0318 11.6646 10.2756 12.1334 10.2756 12.8678C10.2756 13.9209 10.2662 15.224 10.2662 15.4803C10.2662 15.6834 10.41 15.9303 10.807 15.8584C13.9329 14.8209 16.0522 11.8553 16.0522 8.38965C16.0522 4.05527 12.5356 0.764648 8.20002 0.764648ZM3.58621 11.5428C3.54558 11.574 3.55495 11.6459 3.60809 11.7053C3.65811 11.7553 3.73 11.7771 3.77064 11.7365C3.81128 11.7053 3.8019 11.6334 3.74876 11.574C3.69875 11.524 3.62685 11.5021 3.58621 11.5428ZM3.24862 11.2896C3.22674 11.3303 3.25799 11.3803 3.32051 11.4115C3.37053 11.4428 3.43304 11.4334 3.45493 11.3896C3.47681 11.349 3.44555 11.299 3.38303 11.2678C3.32051 11.249 3.2705 11.2584 3.24862 11.2896ZM4.2614 12.4021C4.21139 12.4428 4.23015 12.5365 4.30204 12.5959C4.37394 12.6678 4.46459 12.6771 4.50522 12.6271C4.54586 12.5865 4.5271 12.4928 4.46459 12.4334C4.39582 12.3615 4.30204 12.3521 4.2614 12.4021ZM3.90505 11.9428C3.85504 11.974 3.85504 12.0553 3.90505 12.1271C3.95507 12.199 4.03947 12.2303 4.0801 12.199C4.13012 12.1584 4.13012 12.0771 4.0801 12.0053C4.03634 11.9334 3.95507 11.9021 3.90505 11.9428Z" fill="white" />
                                </svg></a>
                            </div>
                        </div>
                    </div>
                </div>
                <div ref={bottomSecRef} className="bottom-sec">
                    <div className="flex align-center space-between footer-statement">
                        <div className="flex align-center">
                            <img src={footerLogo} />
                            <div>
                                <span className="text-body-1">Contact Us</span>
                                <span className="text-body-1">Privacy</span>
                                <span className="text-body-1">Terms Of Service</span>
                            </div>
                        </div>
                        <span className="text-body">© 2024-2025. All rights reserved.</span>
                    </div>
                </div>
            </div>
        </footer>
    )
}
export default Footer
